import React from 'react';
import { CalendarIcon } from '@radix-ui/react-icons';

import { DateSelectionModal } from '../../features/process/components/date-selection.modal';
import { dateShort } from '../../assets/contants/dates-fns';
import { formatDate } from '../../lib/utils/DateHelper';
import { isValidDateRange } from '../../features/process/is-valid-date-range';
import { asPlainDate, isPlainDate, type PlainDate } from '../../core/services/plain-date';
import { openModal } from '../../core/modal/open-modal';
import { TaskPropertyButton } from '../../features/tasks/components/task-property.button';

interface DateSelectorProps {
  date?: PlainDate | null;
  min?: PlainDate | null;
  max?: PlainDate | null;
  placeholder?: string;
  onUpdate: (value: PlainDate | null) => void;
  field: 'startDate' | 'dueDate';
  disabled?: boolean;
}

export const DateSelector: React.FC<DateSelectorProps> = ({
  date,
  field,
  onUpdate,
  max,
  min,
  placeholder = 'Set date',
  disabled,
}) => {
  // `DateSelectionModal` hands back a raw `<input type="date">` value: a valid `YYYY-MM-DD`
  // string, or `null`/`''` when the native clear icon was used. This is the boundary where a real
  // `PlainDate` is minted — `asPlainDate` is only reached once `isPlainDate` has already confirmed
  // the value is a genuine calendar day, so it never throws on user input. Clearing is a real,
  // API-supported action (the field is nullable end to end), so an empty value is forwarded as
  // `null` rather than swallowed — only a non-empty, malformed value (which the native
  // `<input type="date">` should never actually produce) is dropped in place, since there is no
  // safe `PlainDate` to mint from it and nothing sensible to forward either.
  const handleSubmit = async (value: string | null) => {
    if (!value) {
      onUpdate(null);
      return;
    }

    if (isPlainDate(value)) {
      onUpdate(asPlainDate(value));
    }
  };

  const openEditModal = () => {
    openModal(DateSelectionModal, {
      fieldName: field,
      value: date ?? undefined,
      onSubmit: handleSubmit,
      dateValidator,
    });
  };

  // `min` means the value must fall on or after it; `max`, on or before it.
  const dateValidator = (value: string) => {
    if (min) return isValidDateRange(min, value);
    if (max) return isValidDateRange(value, max);

    return true;
  };

  return (
    <TaskPropertyButton
      isDisabled={disabled}
      isActive={!!date}
      onClick={openEditModal}
      icon={<CalendarIcon />}
      label={date ? formatDate(date, dateShort) : placeholder}
    />
  );
};
